import { useCallback, useEffect, useRef, useState } from "react";
import { useLists } from "modules/lists/context/ListsContext";
import * as listService from "modules/lists/services/listService";
import { CreateListItemPayload } from "modules/lists/types/lists";

const LS_KEY = (listId: string, itemId: string) =>
    `carry:list:${listId}:item:${itemId}`;

const DEBOUNCE_MS = 800;

/**
 * Provides debounced, optimistic autosave for list items.
 *
 * Flow per edit:
 *  1. Immediately applies the change to context (optimistic update).
 *  2. Stores the payload in localStorage as a fallback.
 *  3. Cancels any pending debounce/request for the same item.
 *  4. After 800ms, PUTs the change to the backend via AbortController.
 *  5. On success: clears the localStorage entry and any error state.
 *  6. On failure (non-abort): sets a per-item error message.
 *
 * When `listId` is undefined (e.g. /lists/new), all saves are skipped
 * and only the optimistic context update is applied.
 *
 * @param listId UUID of the active list, or undefined for the new-list flow.
 * @returns `handleEditItem` — call on every item change; `saveErrors` — map of itemId → error message.
 */
const useItemAutosave = (listId: string | undefined) => {
    const { editItem, syncItem } = useLists();

    const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});

    // Refs hold timers and controllers per itemId to avoid re-renders
    const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const controllers = useRef<Record<string, AbortController>>({});

    // Cancel all pending debounces and in-flight requests on unmount
    useEffect(() => {
        return () => {
            Object.values(timers.current).forEach(clearTimeout);
            Object.values(controllers.current).forEach((c) => c.abort());
        };
    }, []);

    /**
     * Applies an optimistic edit and schedules a debounced backend save.
     * @param itemId UUID of the item being edited.
     * @param data Updated item payload.
     */
    const handleEditItem = useCallback(
        (itemId: string, data: CreateListItemPayload) => {
            // 1. Optimistic update — always, regardless of listId
            if (listId) {
                editItem(listId, itemId, data);
            }

            // Skip backend save for the new-list flow
            if (!listId) return;

            // 2. Persist to localStorage as fallback
            try {
                localStorage.setItem(LS_KEY(listId, itemId), JSON.stringify(data));
            } catch {
                // Quota exceeded or private browsing — silently ignore
            }

            // 3. Cancel any pending debounce and in-flight request for this item
            if (timers.current[itemId]) {
                clearTimeout(timers.current[itemId]);
            }
            if (controllers.current[itemId]) {
                controllers.current[itemId].abort();
            }

            // 4. Schedule the backend PUT after the debounce window
            const controller = new AbortController();
            controllers.current[itemId] = controller;

            timers.current[itemId] = setTimeout(async () => {
                try {
                    const updated = await listService.updateItem(listId, itemId, data, controller.signal);

                    // Reconcile local state with the server-authoritative response.
                    // This fixes type, linkedProductName, and catalogProductSku — which the
                    // backend resolves from hashed_sku and may differ from the optimistic values.
                    syncItem(listId, updated);

                    // 5. Success: clear fallback and any previous error
                    try {
                        localStorage.removeItem(LS_KEY(listId, itemId));
                    } catch {
                        // ignore
                    }
                    setSaveErrors((prev) => {
                        if (!prev[itemId]) return prev;
                        const next = { ...prev };
                        delete next[itemId];
                        return next;
                    });
                } catch (err: unknown) {
                    // 6. Only surface genuine failures — ignore deliberate aborts
                    if (err instanceof Error && err.name === "AbortError") return;
                    setSaveErrors((prev) => ({
                        ...prev,
                        [itemId]: "No se pudo guardar",
                    }));
                }
            }, DEBOUNCE_MS);
        },
        [listId, editItem, syncItem],
    );

    return { handleEditItem, saveErrors };
};

export { useItemAutosave };
